import type { CustomerDashboardStats } from "../../../../api/types"
import type {
  DashboardTranslateFn,
  DashboardWidgetDef,
} from "../../../shared/dashboard/DashboardBoardShell"
import { formatInt, type DashboardWidgetKey } from "./customerDashboardStore"
import { ConformityWidget } from "./widgets/ConformityWidget"
import { CoverageWidget } from "./widgets/CoverageWidget"
import { FrameworksWidget } from "./widgets/FrameworksWidget"
import { GapsWidget } from "./widgets/GapsWidget"
import { OpenWidget } from "./widgets/OpenWidget"
import { WaitingWidget } from "./widgets/WaitingWidget"

const MC_BASE = "/material-compliance"

export type TranslateFn = DashboardTranslateFn

export interface DashboardWidgetProps {
  stats: CustomerDashboardStats
  /** The dashboard did not load: `stats` may be the zero fallback. */
  loadFailed: boolean
}

export const DASHBOARD_WIDGETS: Record<
  DashboardWidgetKey,
  DashboardWidgetDef<CustomerDashboardStats>
> = {
  coverage: {
    titleKey: "MC_CSD_W_COVERAGE",
    wide: true,
    component: CoverageWidget,
    // Master-data customers list, not the requests inbox — "All customers"
    // means the customer records, not their requests (mirrors the
    // supplier registry's analogous master-data/suppliers link).
    linkTo: `${MC_BASE}/master-data/customers`,
    linkLabelKey: "MC_CSD_W_COVERAGE_LINK",
  },
  frameworks: {
    titleKey: "MC_CSD_W_FRAMEWORKS",
    component: FrameworksWidget,
    countText: (_s, translate) => translate("MC_CSD_W_FRAMEWORKS_COUNT"),
    // No card-level link — unlike the supplier frameworks widget, the design
    // spec's drill-down map only calls for a header link on Conformity; per
    // framework coverage has no dedicated list view to send it to in v1.
  },
  waiting: {
    titleKey: "MC_CSD_W_WAITING",
    component: WaitingWidget,
    // Top-N-of-total, same entity level (questionnaires) on both sides —
    // mirrors the supplier OutstandingWidget's "N / total" countText.
    countText: (s, _translate, locale) =>
      `${formatInt(s.waitingTop.length, locale)} / ${formatInt(s.statusBuckets.waitingOnYou.count, locale)}`,
    linkTo: `${MC_BASE}/customer-studio/requests?bucket=waiting`,
    // No dedicated MC_CSD_W_WAITING_LINK key exists — reuses the generic
    // "Show all" label, same reuse the supplier registry applies to
    // handedBack/replyQuality (see MC_SSD_W_OUTSTANDING_LINK/MC_SSD_W_IN_REVIEW_LINK).
    linkLabelKey: "MC_CSD_W_SHOW_ALL",
  },
  open: {
    titleKey: "MC_CSD_W_OPEN",
    component: OpenWidget,
    // Fix A (dedupe): openTop's population is now open AND answering_predicate()
    // (the in-flight set, backend: get_open_top / BUCKET_PREDICATES['answering'])
    // — disjoint from waitingTop's open AND waiting population, so the two
    // widgets never repeat the same row. The denominator is therefore the
    // in-flight COUNT (statusBuckets.answering.count, the same
    // answering_predicate()-gated count get_open_top uses), not the shared
    // open_total — "rows shown / in-flight total", not "rows shown /
    // everything open".
    countText: (s, _translate, locale) =>
      `${formatInt(s.openTop.length, locale)} / ${formatInt(s.statusBuckets.answering.count, locale)}`,
    // Drills into the exact `answering` bucket this widget's rows/countText
    // now come from (BUCKET_PREDICATES['answering'] = open_predicate() AND
    // answering_predicate()) — matches get_open_top's population 1:1.
    linkTo: `${MC_BASE}/customer-studio/requests?bucket=answering`,
    linkLabelKey: "MC_CSD_W_SHOW_ALL",
  },
  conformity: {
    titleKey: "MC_CSD_W_CONFORMITY",
    component: ConformityWidget,
    // draft alone — flaggedSvhc is a SUBSET of draft (backend: both count
    // status='draft' rows, see mc_customer_dashboard_repository.get_conformity),
    // so summing the two double-counts the flagged rows.
    // Labelled so the head number is not read as the sum of the rows below
    // (approved and issued are not drafts).
    // `count` picks the plural, `value` is the number as the other widgets
    // print it (thousands separator of the UI language).
    countText: (s, translate, locale) =>
      translate("MC_CSD_W_CONFORMITY_COUNT", {
        count: s.conformity.draft,
        value: formatInt(s.conformity.draft, locale),
      }),
    linkTo: `${MC_BASE}/customer-studio/conformity`,
    linkLabelKey: "MC_CSD_W_SHOW_ALL",
  },
  gaps: {
    titleKey: "MC_CSD_W_GAPS",
    component: GapsWidget,
    // Plain numeric total — mirrors the supplier handedBack widget's
    // countText (sum-of-buckets style would double count here since
    // blockedRequests/openQuestions/stagedInquiries are different entity
    // levels, so this shows the headline blocked-requests count only).
    countText: (s, _translate, locale) =>
      formatInt(s.gaps.blockedRequests, locale),
    linkTo: `${MC_BASE}/customer-studio/requests?bucket=gaps`,
    linkLabelKey: "MC_CSD_W_SHOW_ALL",
  },
}
